import React, { useCallback, useState, useMemo } from 'react'
import { RegisterItemPropertyFormItemProps } from '@modules/registers/items/form/types'
import { ParentRegister } from '@core/entities/register/ParentRegister'
import { RegisterTemplateProperty } from '@core/entities/register/RegisterTemplateProperty'
import {
  Control,
  Controller,
  FieldErrors,
  useFormContext,
} from 'react-hook-form'
import { useGetRegisterItems } from '@core/react-query/features/registers/registers/register/items/hooks'
import { useGetRegisterItem } from '@core/react-query/features/registers/registers/register/items/item'
import _debounce from 'lodash/debounce'
import { Flex, Text } from '@fundamentals'
import { Autocomplete } from '@mui/material'
import { MenuItem, TextField } from '@common/material'
import { localizedStrings } from '@core/strings'
import { RegisterItem } from '@core/entities/register/RegisterItem'
import moment from 'moment/moment'

export const RegisterItemLinkPropertyFormItem: React.FC<
  RegisterItemPropertyFormItemProps
> = ({ property, disabled, registerItem, register }) => {
  const {
    control,
    getValues,
    formState: { errors },
  } = useFormContext()
  const linkValue = parseInt(getValues(`${property.id}`)?.parentRegisterItemId)
  const parentRegister = register?.parentRegisters.find(
    (parentRegister) => parentRegister.childProperty.id === property.id,
  )
  const isDisabled =
    disabled || (!!registerItem && property.lockedAfterCreation)

  if (parentRegister)
    return (
      <RegisterLinkCreateItem
        parentRegister={parentRegister}
        property={property}
        control={control}
        disabled={isDisabled}
        errors={errors}
        required={property.required}
        initialValue={linkValue}
      />
    )
}

type PropertyProps = {
  parentRegister: ParentRegister
  property: RegisterTemplateProperty
  control: Control
  errors: FieldErrors
  required: boolean
  initialValue?: number
  disabled?: boolean
}

const RegisterLinkCreateItem: React.FC<PropertyProps> = ({
  parentRegister,
  property,
  control,
  errors,
  disabled,
  required,
  initialValue,
}) => {
  const [term, setTerm] = useState(null)

  const { data: linkRegisterItemsPage } = useGetRegisterItems(
    {
      registerId: parentRegister?.parentRegister?.id,
      term,
    },
    {
      enabled: !!parentRegister?.parentRegister?.id,
    },
  )

  const linkRegisterItemsPageContent = useMemo(
    () => linkRegisterItemsPage?.pages.flatMap((page) => page.content),
    [linkRegisterItemsPage?.pages],
  )

  const { data: initialLinkItem } = useGetRegisterItem(
    {
      registerId: parentRegister?.parentRegister?.id,
      registerItemId: initialValue,
    },
    {
      enabled: !!initialValue,
    },
  )

  const debouncedSearch = useCallback(
    _debounce((term) => setTerm(term), 300),
    [],
  )

  const mappedPropertyIds = property?.parentTemplate?.properties?.map(
    (p) => `${p.id}`,
  )

  return (
    <Flex flexDirection='column'>
      <Controller
        control={control}
        name={`${parentRegister.childProperty.id}`}
        rules={{
          validate: (value) =>
            !!value?.parentRegisterItemId || !property.required,
        }}
        render={({ field: { onChange, value }, formState: { errors } }) => (
          <Autocomplete
            options={linkRegisterItemsPageContent ?? []}
            title={parentRegister.childProperty.name}
            data-test={`${property.type}-${property.name}`}
            disabled={disabled}
            value={
              linkRegisterItemsPageContent?.find(
                (item) => item.id === value?.parentRegisterItemId,
              ) ||
              initialLinkItem ||
              ''
            }
            renderInput={(params) => (
              <TextField
                {...params}
                InputLabelProps={{ shrink: true }}
                placeholder={localizedStrings.typeOrSelect}
                label={parentRegister.childProperty.name}
                required={required}
                onChange={(e) => debouncedSearch(e.target.value)}
                error={
                  !!Object.keys(errors).includes(
                    `${parentRegister.childProperty.id}`,
                  )
                }
                sx={{ mb: 2 }}
              />
            )}
            onChange={(_, value: RegisterItem) =>
              onChange({
                ...property,
                registerPropertyId: property.id,
                parentRegisterItemId: value?.id,
              })
            }
            getOptionLabel={(option: RegisterItem) => {
              const parts = option.data
                ?.filter((item) =>
                  mappedPropertyIds.includes(item.registerPropertyId),
                )
                ?.map((item) => {
                  switch (item.type) {
                    case 'STRING':
                      return `${item.textValue}`
                    case 'DATE':
                      return `${moment(item.dateValue).format('D MMM YYYY')}`
                    case 'NUMBER':
                      return `${item.numberValue}`
                    case 'SELECT':
                      return `${item.selectedOption.label}`
                    default:
                      return ''
                  }
                })
                .filter((part) => !!part)

              return parts?.join(',') || ''
            }}
            // All filtering is done on the backend, this removes the client side filtering
            filterOptions={(options, _) => options}
            renderOption={(props, option: RegisterItem) => (
              <MenuItem
                key={option.id}
                value={option.id}
                data-test={`LINK-${props['data-option-index']}`}
                {...props}
              >
                <Flex flexDirection='column'>
                  {option.data
                    .filter((item) =>
                      mappedPropertyIds.includes(item.registerPropertyId),
                    )
                    .map((item, index) => {
                      let value = null
                      switch (item.type) {
                        case 'STRING':
                          value = item.textValue
                          break
                        case 'DATE':
                          value = moment(item.dateValue).format('D MMM YYYY')
                          break
                        case 'NUMBER':
                          value = item.numberValue
                          break
                        case 'SELECT':
                          value = item.selectedOption.label
                          break
                        default:
                          value = ''
                      }
                      return (
                        <Flex
                          key={index}
                          data-test={`${item.type}-${item.name}-${index}`}
                        >
                          <Text>{`${item.name}: ${value}`}</Text>
                        </Flex>
                      )
                    })}
                </Flex>
              </MenuItem>
            )}
          />
        )}
      />
    </Flex>
  )
}
